import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { Turnstile, TURNSTILE_ENABLED } from "@/components/Turnstile";
import { useCart } from "@/context/CartContext";
import { customerApi, formatGBP, formatApiError } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { toast } from "sonner";
import {
  User, Package, MapPin, Heart, Clock, Settings, LogOut, Plus, Trash2, CheckCircle2, Sparkles, Download,
} from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview", icon: User },
  { key: "orders", label: "Orders", icon: Package },
  { key: "addresses", label: "Addresses", icon: MapPin },
  { key: "wishlist", label: "Wishlist", icon: Heart },
  { key: "recent", label: "Recently Viewed", icon: Clock },
  { key: "settings", label: "Settings", icon: Settings },
];

const STATUS_LABEL = {
  pending_payment: "Awaiting Payment", new: "Order Confirmed", processing: "Processing",
  dispatched: "Dispatched", shipped: "Shipped", out_for_delivery: "Out for Delivery",
  delivered: "Delivered", completed: "Completed", cancelled: "Cancelled",
};

// Which of the 3 timeline steps the order has reached (0=placed, 1=dispatched, 2=delivered).
const timelineStep = (status) => {
  if (["delivered", "completed"].includes(status)) return 2;
  if (["dispatched", "shipped", "out_for_delivery"].includes(status)) return 1;
  return 0; // new / processing
};

const OrderTimeline = ({ status }) => {
  if (["pending_payment", "cancelled"].includes(status)) return null;
  const steps = ["Placed", "Dispatched", "Delivered"];
  const current = timelineStep(status);
  return (
    <div className="mt-4 flex items-center" data-testid="order-timeline">
      {steps.map((label, i) => {
        const done = i <= current;
        return (
          <div key={label} className={`flex items-center ${i < steps.length - 1 ? "flex-1" : ""}`}>
            <div className="flex flex-col items-center">
              <div className={`w-2.5 h-2.5 rounded-full ${done ? "bg-pink" : "bg-gray-300"}`} />
              <span className={`mt-1.5 text-[11px] ${done ? "text-charcoal" : "text-gray-400"}`}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-px mx-2 mb-4 ${i < current ? "bg-pink" : "bg-gray-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default function Account() {
  const { customer, logout, refreshCustomer } = useCustomerAuth();
  const { wishlist } = useCart();
  const [tab, setTab] = useState("overview");
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [recent, setRecent] = useState([]);
  const [wishProducts, setWishProducts] = useState([]);

  useEffect(() => {
    if (!customer) return;
    customerApi().get("/account/orders").then(({ data }) => setOrders(data)).catch(() => {});
    customerApi().get("/account/addresses").then(({ data }) => setAddresses(data)).catch(() => {});
    customerApi().get("/account/recently-viewed").then(({ data }) => setRecent(data.products)).catch(() => {});
    customerApi().get("/account/wishlist").then(({ data }) => setWishProducts(data.products)).catch(() => {});
  }, [customer, wishlist]);

  if (customer === null) return <div className="min-h-[70vh] flex items-center justify-center text-gray-400">Loading...</div>;
  if (customer === false) return <Navigate to="/login?redirect=/account" replace />;

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-12 min-h-[70vh]" data-testid="account-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.28em] text-pink">My Account</p>
        <h1 className="font-serif text-4xl lg:text-5xl mt-2 text-charcoal">Hello, {customer.name || "there"}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        <aside className="lg:border-r lg:border-gray-100 lg:pr-6">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setTab(key)} data-testid={`account-tab-${key}`}
                className={`flex items-center gap-3 px-4 py-3 text-sm whitespace-nowrap transition-colors ${tab === key ? "bg-pink/5 text-pink" : "text-gray-600 hover:text-pink"}`}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
            <button onClick={logout} data-testid="account-logout"
              className="flex items-center gap-3 px-4 py-3 text-sm text-gray-600 hover:text-pink transition-colors">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </nav>
        </aside>

        <main>
          {tab === "overview" && <Overview customer={customer} orders={orders} wishCount={wishProducts.length} onNav={setTab} />}
          {tab === "orders" && <Orders orders={orders} />}
          {tab === "addresses" && <Addresses addresses={addresses} setAddresses={setAddresses} />}
          {tab === "wishlist" && <ProductGrid products={wishProducts} empty="Your wishlist is empty." />}
          {tab === "recent" && <ProductGrid products={recent} empty="No recently viewed products yet." />}
          {tab === "settings" && <SettingsTab customer={customer} refreshCustomer={refreshCustomer} />}
        </main>
      </div>
    </div>
  );
}

const Overview = ({ customer, orders, wishCount, onNav }) => {
  const rewards = Number(customer.rewards_balance || 0);
  return (
  <div data-testid="account-overview" className="space-y-4">
    <div className="rounded-lg p-6 sm:p-7 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #2a1622 0%, #4a2036 55%, #6d2a4c 100%)" }} data-testid="account-rewards-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-white/60">Your Rewards Credit</p>
          <p className="font-serif text-4xl sm:text-5xl mt-2" style={{ background: "linear-gradient(180deg,#f8ecbc 0%,#edd48c 40%,#d9b45f 60%,#f4e2a4 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }} data-testid="account-rewards-balance">
            {formatGBP(rewards)}
          </p>
          <p className="text-xs text-white/60 mt-2">Earn £1 for every £25 you spend. Redeem it at checkout.</p>
        </div>
        <Sparkles className="w-7 h-7 text-metallic-gold shrink-0" />
      </div>
      {rewards > 0 && (
        <Link to="/shop/all" className="inline-block mt-4 bg-white/95 text-charcoal px-6 py-2.5 rounded-full text-xs uppercase tracking-[0.16em] hover:bg-white transition-colors" data-testid="account-rewards-shop">
          Shop &amp; Redeem
        </Link>
      )}
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <button onClick={() => onNav("orders")} className="text-left bg-surface p-6 hover:shadow-md transition-shadow">
        <Package className="w-6 h-6 text-gold" />
        <p className="text-3xl font-serif mt-3">{orders.length}</p>
        <p className="text-sm text-gray-500">Orders</p>
      </button>
      <button onClick={() => onNav("wishlist")} className="text-left bg-surface p-6 hover:shadow-md transition-shadow">
        <Heart className="w-6 h-6 text-gold" />
        <p className="text-3xl font-serif mt-3">{wishCount}</p>
        <p className="text-sm text-gray-500">Wishlist Items</p>
      </button>
      <div className="bg-surface p-6">
        <User className="w-6 h-6 text-gold" />
        <p className="text-sm mt-3 text-charcoal">{customer.email}</p>
        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
          {customer.verified ? <><CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> Verified</> : "Email not verified"}
        </p>
      </div>
    </div>
  </div>
  );
};

const downloadBlob = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); window.URL.revokeObjectURL(url);
};

const Orders = ({ orders }) => {
  const [busy, setBusy] = useState(null);
  const getInvoice = async (o) => {
    setBusy(o.order_number);
    try {
      const { data } = await customerApi().get(`/account/orders/${o.order_number}/invoice`, { responseType: "blob" });
      downloadBlob(data, `${o.order_number}-invoice.pdf`);
    } catch {
      toast.error("Invoice not available yet");
    } finally { setBusy(null); }
  };
  return (
  <div data-testid="account-orders">
    {orders.length === 0 ? (
      <EmptyState text="You haven't placed any orders yet." cta />
    ) : (
      <div className="space-y-4">
        {orders.map((o) => (
          <div key={o.order_number} className="border border-gray-100 p-5" data-testid={`order-${o.order_number}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-serif text-lg">{o.order_number}</p>
                <p className="text-xs text-gray-400">{new Date(o.created_at).toLocaleDateString("en-GB")}</p>
              </div>
              <div className="text-right">
                <span className="text-xs uppercase tracking-wide bg-pink/10 text-pink px-3 py-1">{STATUS_LABEL[o.status] || o.status}</span>
                <p className="text-lg font-medium mt-1">{formatGBP(o.total)}</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-2 text-sm text-gray-500">
              {o.items.map((it) => <span key={it.product_id}>{it.name} ×{it.quantity}</span>).reduce((a, b) => [a, " · ", b])}
            </div>
            <OrderTimeline status={o.status} />
            {o.tracking_number && <p className="text-xs text-gray-500 mt-2">Tracking: {o.tracking_number}</p>}
            {o.status !== "pending_payment" && (
              <button
                onClick={() => getInvoice(o)}
                disabled={busy === o.order_number}
                data-testid={`download-invoice-${o.order_number}`}
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-pink border border-pink/40 rounded-full px-4 py-1.5 hover:bg-pink/5 transition-colors disabled:opacity-50"
              >
                <Download className="w-3.5 h-3.5" /> {busy === o.order_number ? "Preparing…" : "Download Invoice"}
              </button>
            )}
          </div>
        ))}
      </div>
    )}
  </div>
  );
};

const Addresses = ({ addresses, setAddresses }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ label: "Home", full_name: "", phone: "", address: "", city: "", postcode: "", country: "United Kingdom", is_default: false });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await customerApi().post("/account/addresses", form);
      setAddresses(data);
      setShowForm(false);
      setForm({ label: "Home", full_name: "", phone: "", address: "", city: "", postcode: "", country: "United Kingdom", is_default: false });
      toast.success("Address saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    const { data } = await customerApi().delete(`/account/addresses/${id}`);
    setAddresses(data);
    toast.message("Address removed");
  };

  return (
    <div data-testid="account-addresses">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {addresses.map((a) => (
          <div key={a.id} className="border border-gray-100 p-5" data-testid={`address-${a.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-pink">{a.label}{a.is_default ? " · Default" : ""}</span>
              <button onClick={() => remove(a.id)} data-testid={`address-delete-${a.id}`} className="text-gray-400 hover:text-pink"><Trash2 className="w-4 h-4" /></button>
            </div>
            <p className="font-medium mt-2">{a.full_name}</p>
            <p className="text-sm text-gray-500">{a.address}, {a.city}, {a.postcode}</p>
            <p className="text-sm text-gray-500">{a.country}{a.phone ? ` · ${a.phone}` : ""}</p>
          </div>
        ))}
      </div>

      {showForm ? (
        <form onSubmit={save} className="mt-6 border border-gray-100 p-5 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="address-form">
          {[["label", "Label"], ["full_name", "Full Name"], ["phone", "Phone"], ["postcode", "Postcode"], ["address", "Address"], ["city", "City"], ["country", "Country"]].map(([k, l]) => (
            <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
              <label className="text-xs uppercase tracking-[0.12em] text-gray-500">{l}</label>
              <input required={["full_name", "address", "city", "postcode"].includes(k)} value={form[k]} onChange={(e) => upd(k, e.target.value)}
                data-testid={`address-field-${k}`} className="mt-1.5 w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-pink" />
            </div>
          ))}
          <label className="flex items-center gap-2 text-sm text-gray-600 sm:col-span-2">
            <input type="checkbox" checked={form.is_default} onChange={(e) => upd("is_default", e.target.checked)} /> Set as default address
          </label>
          <div className="sm:col-span-2 flex gap-3">
            <button type="submit" data-testid="address-save" className="bg-pink text-white px-6 py-3 text-xs uppercase tracking-[0.15em] hover:bg-pink-hover transition-colors">Save Address</button>
            <button type="button" onClick={() => setShowForm(false)} className="text-gray-500 px-4 text-sm">Cancel</button>
          </div>
        </form>
      ) : (
        <button onClick={() => setShowForm(true)} data-testid="add-address-btn"
          className="mt-6 flex items-center gap-2 border border-charcoal text-charcoal px-6 py-3 text-xs uppercase tracking-[0.15em] hover:bg-pink hover:border-pink hover:text-white transition-colors">
          <Plus className="w-4 h-4" /> Add Address
        </button>
      )}
    </div>
  );
};

const SettingsTab = ({ customer, refreshCustomer }) => {
  const { changePassword } = useCustomerAuth();
  const [name, setName] = useState(customer.name || "");
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [token, setToken] = useState("");
  const [resetSignal, setResetSignal] = useState(0);

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      await customerApi().put("/account/profile", { name });
      await refreshCustomer();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (TURNSTILE_ENABLED && !token) {
      toast.error("Please complete the human verification.");
      return;
    }
    try {
      await changePassword(pw.current_password, pw.new_password, token);
      setPw({ current_password: "", new_password: "" });
      toast.success("Password changed");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setResetSignal((n) => n + 1); // Turnstile tokens are single-use
    }
  };

  return (
    <div className="max-w-md space-y-10" data-testid="account-settings">
      <form onSubmit={saveProfile} className="space-y-4">
        <h3 className="font-serif text-xl">Profile</h3>
        <div>
          <label className="text-xs uppercase tracking-[0.12em] text-gray-500">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name"
            className="mt-1.5 w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-pink" />
        </div>
        <button type="submit" data-testid="settings-save-profile" className="bg-pink text-white px-6 py-3 text-xs uppercase tracking-[0.15em] hover:bg-pink-hover transition-colors">Save</button>
      </form>

      {customer.auth_provider === "password" && (
        <form onSubmit={savePassword} className="space-y-4">
          <h3 className="font-serif text-xl">Change Password</h3>
          <input type="password" required placeholder="Current password" value={pw.current_password}
            onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} data-testid="settings-current-password"
            className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-pink" />
          <input type="password" required placeholder="New password" value={pw.new_password}
            onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} data-testid="settings-new-password"
            className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-pink" />
          <Turnstile onVerify={setToken} action="change-password" resetSignal={resetSignal} />
          <button type="submit" data-testid="settings-save-password" className="bg-pink text-white px-6 py-3 text-xs uppercase tracking-[0.15em] hover:bg-pink-hover transition-colors">Update Password</button>
        </form>
      )}
    </div>
  );
};

const ProductGrid = ({ products, empty }) =>
  products.length === 0 ? (
    <EmptyState text={empty} cta />
  ) : (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
      {products.map((p) => <ProductCard key={p.id} product={p} />)}
    </div>
  );

const EmptyState = ({ text, cta }) => (
  <div className="text-center py-16">
    <p className="text-gray-500">{text}</p>
    {cta && <Link to="/shop/all" className="inline-block mt-6 bg-pink text-white px-8 py-3.5 text-xs uppercase tracking-[0.18em] hover:bg-pink-hover transition-colors">Explore Collections</Link>}
  </div>
);
